import { variantProvider } from '@/app'
import { Icon, Text, Touchable, View } from '@/components'
import { useBooleanToggle } from '@codeleap/common'

type OverlayLessonLearnProps = {
  disabled?: boolean
  isRtl?: boolean
  image_alt_text?: string
  has_translated_image: boolean
}

export const OverlayLessonLearn = ({
  disabled,
  isRtl,
  image_alt_text,
  has_translated_image,
}: OverlayLessonLearnProps) => {
  const [expanded, toggle] = useBooleanToggle(!has_translated_image)

  if (disabled) return null

  return (
    <View style={[styles.wrapper, !expanded && styles.wrapperCollapsed]}>
      {expanded && (
        <View style={[styles.content, isRtl && styles.contentRTL]}>
          <Text
            text={image_alt_text}
            variants={['p1', 'color:neutral1']}
            style={[styles.descriptionText, isRtl ? styles.textRTL : undefined]}
          />
        </View>
      )}

      {expanded && <View style={styles.divider} />}

      <Touchable
        debugName='TranslationOverlay toggle'
        onPress={toggle}
        style={[styles.footer, isRtl ? styles.footerRTL : styles.footerLTR]}
      >
        <Icon name='translation-icon' variants={['iconSize:3']} debugName={'TranslationOverlay icon'} />
        <Icon
          name='toggle-icon'
          variants={['iconSize:3']}
          debugName={'TranslationOverlay toggle icon'}
          style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}
        />
      </Touchable>
    </View>
  )
}

const styles = variantProvider.createComponentStyle(
  theme => ({
    wrapper: {
      ...theme.presets.fullWidth,
      ...theme.presets.column,
      backgroundColor: theme.colors.overlay,
      backdropFilter: 'blur(2px)',
      overflow: 'hidden',
    },
    wrapperCollapsed: {
      width: 'fit-content',
    },
    content: {
      ...theme.presets.column,
      ...theme.spacing.padding(2),
      ...theme.spacing.gap(0.5),
      direction: 'ltr',
      maxHeight: '50svh',
      overflowY: 'auto',
    },
    contentRTL: {
      alignItems: 'flex-end',
    },
    descriptionText: {
      ...theme.spacing.marginTop(1),
      whiteSpace: 'pre-line',
    },
    textRTL: {
      textAlign: 'right',
    },
    divider: {
      ...theme.presets.fullWidth,
      height: 1,
      backgroundColor: theme.colors.neutral5,
      ...theme.spacing.marginHorizontal(2),
      width: 'auto',
    },
    footer: {
      ...theme.presets.fullWidth,
      ...theme.presets.row,
      ...theme.presets.alignCenter,
      ...theme.spacing.padding(2),
      ...theme.spacing.gap(0.5),
    },
    footerLTR: {
      ...theme.presets.justifyStart,
      flexDirection: 'row',
    },
    footerRTL: {
      ...theme.presets.justifyStart,
      flexDirection: 'row-reverse',
    },
  }),
  true,
)
